import React, { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";

import * as api from "../api";
import CTAPopup from "../components/Event/CTAPopup";
import Loader from "../components/Utils/Loader";
import TableRequestGuestFields from "../components/TableRequests/TableRequestGuestFields";
import TableRequestYouCard from "../components/TableRequests/TableRequestYouCard";
import { ReactComponent as AlertIcon } from "../assets/images/input-error.svg";
import { ReactComponent as PlusIcon } from "../assets/images/plus-black.svg";
import { useLoginGate } from "../utils/hooks";
import {
  guestFieldErrors,
  tableRequestErrorMessage,
} from "../utils/tableRequest";

// The backend's minimum party is the requester plus three guests, so these
// rows are never removable.
const REQUIRED_GUESTS = 3;

const emptyGuest = () => ({
  firstName: "",
  lastName: "",
  email: "",
  mobileNumber: "",
  gender: "",
});

const RequestTable = ({
  user = {},
  userLoading = false,
  setShowLogin = () => {},
}) => {
  const { id } = useParams();
  const history = useHistory();
  const isSignedIn = useLoginGate(user, userLoading, setShowLogin);
  const [eventName, setEventName] = useState("");
  const [maxTableSize, setMaxTableSize] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const alertRef = useRef(null);
  const inFlight = useRef(false);
  const focusTarget = useRef(null);

  const {
    register,
    control,
    handleSubmit,
    watch,
    getValues,
    setError,
    setFocus,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      notes: "",
      guests: Array.from({ length: REQUIRED_GUESTS }, emptyGuest),
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: "guests" });
  const guestValues = watch("guests");
  const tableIsFull = Boolean(maxTableSize) && fields.length + 1 >= maxTableSize;

  useEffect(() => {
    if (!isSignedIn) return undefined;
    let ignore = false;
    api.events
      .getEventById(id)
      .then((response) => {
        if (ignore) return;
        setEventName(response?.data?.name || "");
        setMaxTableSize(response?.data?.maxTableSize || null);
      })
      .catch(() => {
        if (!ignore) setEventName("");
      });
    return () => {
      ignore = true;
    };
  }, [id, isSignedIn]);

  useEffect(() => {
    const target = focusTarget.current;
    if (!target) return;
    focusTarget.current = null;
    if (target === "alert") alertRef.current?.focus();
    else setFocus(target);
  });

  const submit = ({ notes, guests }) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setFormError("");
    api.tableRequests
      .createTableRequest({ event: Number(id), notes, guests })
      .then((response) => {
        history.push(`/table-requests/${response.data.id}`);
      })
      .catch((error) => {
        const fieldErrors = guestFieldErrors(error);
        fieldErrors.forEach(({ index, field, message }) =>
          setError(`guests.${index}.${field}`, { type: "server", message })
        );
        focusTarget.current = fieldErrors.length
          ? `guests.${fieldErrors[0].index}.${fieldErrors[0].field}`
          : "alert";
        setFormError(tableRequestErrorMessage(error));
        inFlight.current = false;
        setSubmitting(false);
      });
  };

  if (!isSignedIn) {
    return (
      <div className="table-request-form">
        <span className="table-request-form__eyebrow">Request a table</span>
        {userLoading ? (
          <Loader />
        ) : (
          <div className="table-request-form__signin">
            <p>Sign in to request a table.</p>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setShowLogin(true)}
            >
              Sign in
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="table-request-form">
      <span className="table-request-form__eyebrow">Request a table</span>
      <h2 className="page-title">{eventName || "Request a table"}</h2>
      <p className="table-request-form__intro">
        Tell us who's coming and our team will email you with the table we can
        offer.
      </p>
      <p className="table-request-form__size">
        {maxTableSize
          ? `Tables seat up to ${maxTableSize} people, you included. Bring at least ${REQUIRED_GUESTS} guests and up to ${maxTableSize - 1}.`
          : `Bring at least ${REQUIRED_GUESTS} guests along with you.`}
      </p>

      <form
        id="table-request-form"
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        {formError ? (
          <div
            className="table-request-form__error"
            role="alert"
            tabIndex={-1}
            ref={alertRef}
          >
            <AlertIcon aria-hidden="true" />
            {formError}
          </div>
        ) : null}

        <TableRequestYouCard user={user} />

        {fields.map((field, index) => (
          <TableRequestGuestFields
            key={field.id}
            index={index}
            register={register}
            control={control}
            errors={errors.guests?.[index]}
            gender={guestValues?.[index]?.gender}
            onRemove={index >= REQUIRED_GUESTS ? () => remove(index) : null}
            getValues={getValues}
            ownEmail={user.email}
            ownPhone={user.mobile}
          />
        ))}

        <p className="table-request-form__party" aria-live="polite">
          {maxTableSize
            ? `Your party: ${fields.length + 1} of ${maxTableSize}`
            : `Your party: ${fields.length + 1}`}
        </p>

        {tableIsFull ? (
          <p className="table-request-form__full">
            That's a full table. Remove a guest to add someone else.
          </p>
        ) : (
          <button
            type="button"
            className="table-request-form__add"
            onClick={() => append(emptyGuest())}
          >
            <PlusIcon aria-hidden="true" />
            Add guest
          </button>
        )}

        <div className="table-request-form__field table-request-form__field--notes">
          <label className="table-request-form__label" htmlFor="table-request-notes">
            Note <span className="table-request-form__optional">(optional)</span>
          </label>
          <textarea
            id="table-request-notes"
            className="registration-form__input table-request-form__notes"
            rows={3}
            placeholder="Occasion, preferences, anything we should know"
            {...register("notes")}
          />
        </div>
      </form>

      <CTAPopup
        actionHandler={handleSubmit(submit)}
        text={submitting ? "Sending…" : "Send request"}
        disabled={submitting}
        loading={submitting}
      />
    </div>
  );
};

export default RequestTable;
